import { Button, Stack, Tooltip, Typography } from '@mui/material';
import {
  AlertButton,
  LoadingButton,
  Show,
  toast,
  WarningIcon,
} from '@side/pantry';
import { useAtomValue } from 'jotai';
import NextLink from 'next/link';
import { useCallback, useState } from 'react';
import { isUploadingAttachment } from 'src/common/atoms/documents';
import { useRunner } from 'src/common/hooks/runners/useRunner';
import { LINK_LABELS, TXM_BUYERS_PATH } from 'src/constants';
import type { GQLInteractionType } from 'src/generated/graph-api';
import { GenerateOfferModal } from 'src/views/Documents/components/GenerateOfferModal';
import { ViewOfferModal } from 'src/views/Documents/components/ViewOfferModal';
import { BuyerAgreementStatus } from '@/views/CreateOffer/hooks/useBuyerAgreementStatus';
import { useInteractionLockedContext } from '@/views/Interactions/context/InteractionLockedContext';
import { TrackDocumentsEvent } from '$analytics/useTrackAnalytics/domains/documents/events';
import { useTrackDocsAnalytics } from '$analytics/useTrackAnalytics/domains/documents/useTrackDocsAnalytics';
import { useGetOfferButtons } from './useOfferButtons';

interface Props {
  id: string;
  type: GQLInteractionType;
}

export const OfferButtons = ({ id, type }: Props) => {
  const trackEvent = useTrackDocsAnalytics();
  const { isLocked } = useInteractionLockedContext();
  const uploadingAttachment = useAtomValue(isUploadingAttachment);
  const [generateOfferModalOpen, setGenerateOfferModalOpen] = useState(false);
  const [viewOfferModalOpen, setViewOfferModalOpen] = useState(false);
  const [isGeneratingOffer, setIsGeneratingOffer] = useState(false);
  const {
    loading,
    refetch: refetchOfferButtons,
    canGenerateOffer,
    canViewOfferDocuments,
    disabledTooltip,
    buyer,
    buyerAgreementStatus,
  } = useGetOfferButtons();

  const { execute: executeGenerateOffer } = useRunner({
    defaultErrorMessage: 'There was an error generating the offer',
  });

  // Failure toasts and analytics live in GenerateOfferModal, which awaits
  // this promise — rethrow so it can handle them.
  const handleGenerateOffer = useCallback(
    async (documentIds: string[]) => {
      setIsGeneratingOffer(true);
      try {
        await executeGenerateOffer({
          type: 'GENERATE_OFFER',
          interactionId: id,
          requestData: { documentIds },
        });
        refetchOfferButtons();
        toast('success', 'Offer package successfully generated!', {
          showCloseButton: true,
        });
        setViewOfferModalOpen(true);
      } finally {
        setIsGeneratingOffer(false);
      }
    },
    [executeGenerateOffer, id, refetchOfferButtons],
  );

  return (
    <Stack
      spacing={2}
      sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
    >
      <GenerateOfferModal
        isUpdatingOffer={canViewOfferDocuments ?? false}
        interactionId={id}
        interactionType={type}
        open={generateOfferModalOpen}
        generateOffer={handleGenerateOffer}
        handleClose={(analyticsInfo: string) => {
          setGenerateOfferModalOpen(false);
          trackEvent(TrackDocumentsEvent.CloseGenerateOfferModal, {
            envelope_ids: analyticsInfo,
          });
        }}
      />
      <ViewOfferModal
        interactionId={id}
        interactionType={type}
        open={viewOfferModalOpen}
        handleClose={() => {
          setViewOfferModalOpen(false);
          trackEvent(TrackDocumentsEvent.CloseViewOfferModal);
        }}
      />
      <Show
        when={
          !isLocked &&
          buyer &&
          buyerAgreementStatus !== BuyerAgreementStatus.SIGNED
        }
      >
        <Stack
          sx={{
            width: '100%',
            backgroundColor: 'grey.200',
            border: '1px solid',
            borderColor: 'grey.200',
            p: 2,
            pt: '10px',
            pb: '10px',
            borderRadius: '2px',
          }}
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 2 }}>
            <WarningIcon sx={{ mr: 1, height: '20px', width: '20px' }} />
            <Typography variant="subtitle2">Can&apos;t Generate</Typography>
          </Stack>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {buyerAgreementStatus === BuyerAgreementStatus.EXPIRED
              ? 'Buyer agreement is expired. Update the end date to continue.'
              : 'A signed buyer agreement is required to generate an offer'}
          </Typography>
          <AlertButton
            variant="outlined"
            severity="info"
            fullWidth
            href={
              buyerAgreementStatus === BuyerAgreementStatus.EXPIRED
                ? `${TXM_BUYERS_PATH}/${buyer?.id}/buyer-agreement`
                : `${TXM_BUYERS_PATH}/${buyer?.id}/documents`
            }
            LinkComponent={NextLink}
            sx={{
              color: 'warning.contrastText',
              borderColor: 'warning.contrastText',
            }}
          >
            Manage Agreement
          </AlertButton>
        </Stack>
      </Show>

      {!isLocked && (
        <Tooltip
          arrow
          placement="top"
          title={disabledTooltip}
          slotProps={{
            popper: {
              sx: {
                textAlign: 'center',
                maxWidth: (theme) => theme.spacing(24),
              },
            },
          }}
        >
          <span style={{ width: '100%' }}>
            <LoadingButton
              variant="contained"
              fullWidth
              disabled={!canGenerateOffer || uploadingAttachment}
              loading={loading || isGeneratingOffer}
              defaultText={
                canViewOfferDocuments ? 'UPDATE OFFER' : 'GENERATE OFFER'
              }
              loadingText={isGeneratingOffer ? 'GENERATING...' : 'LOADING...'}
              onClick={() => {
                if (canViewOfferDocuments) {
                  trackEvent(TrackDocumentsEvent.UpdateOfferButtonClick);
                } else {
                  trackEvent(TrackDocumentsEvent.GenerateOfferButtonClick);
                }
                setGenerateOfferModalOpen(true);
              }}
            />
          </span>
        </Tooltip>
      )}
      {canViewOfferDocuments && (
        <Button
          variant="text"
          fullWidth
          disabled={uploadingAttachment}
          onClick={() => {
            trackEvent(TrackDocumentsEvent.ViewOfferDocumentsClick);
            setViewOfferModalOpen(true);
          }}
        >
          {LINK_LABELS.OFFER_DOCUMENTS}
        </Button>
      )}
    </Stack>
  );
};
